import { useState, type FormEvent } from 'react'
import type { Result } from '../api/client'
import type { Mission } from '../api/types'
import { Modal } from './Modal'

export interface NewEventBody {
  name: string
  locationName?: string
  startsAt?: string
  centreLatitude?: number
  centreLongitude?: number
  factions?: { key: string; name: string; displayOrder: number }[]
}

/**
 * Put on an event.
 *
 * Asks for a name and nothing else that is genuinely required. An organiser creating a game three
 * weeks out does not yet know its centre, and a form that insists on one is a form filled with
 * placeholder coordinates.
 */
export function NewEventForm({
  onCreate,
  onCancel,
}: {
  onCreate: (body: NewEventBody) => Promise<Result<Mission>>
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [locationName, setLocationName] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [centre, setCentre] = useState('')
  const [factions, setFactions] = useState('BLUFOR, OPFOR')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    const parsedCentre = parseCentre(centre)
    if (centre.trim() && !parsedCentre) {
      setBusy(false)
      setError('Centre should be two numbers, such as 45.5416, 10.2118.')
      return
    }

    const result = await onCreate({
      name: name.trim(),
      locationName: locationName.trim() || undefined,
      startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
      centreLatitude: parsedCentre?.[0],
      centreLongitude: parsedCentre?.[1],
      factions: parseFactions(factions),
    })

    setBusy(false)
    if (!result.ok) setError(result.error.message)
  }

  return (
    <Modal title="New event" onClose={onCancel}>
      <form className="stack" onSubmit={submit}>
        <div className="field">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Operation Winter"
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="location">Location (optional)</label>
          <input
            id="location"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            placeholder="Cava Nord, Brescia"
          />
        </div>

        <div className="field">
          <label htmlFor="starts">Starts (optional)</label>
          <input
            id="starts"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="centre">Map centre (optional)</label>
          <input
            id="centre"
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
            placeholder="45.5416, 10.2118"
          />
          <p className="tertiary" style={{ marginTop: 6 }}>
            Where the map opens for everyone. You can set it later by dragging the TOC map.
          </p>
        </div>

        <div className="field">
          <label htmlFor="factions">Sides</label>
          <input
            id="factions"
            value={factions}
            onChange={(e) => setFactions(e.target.value)}
            placeholder="BLUFOR, OPFOR"
          />
          <p className="tertiary" style={{ marginTop: 6 }}>
            Comma separated. Teams are put into these when they join.
          </p>
        </div>

        {error && <div className="banner danger">{error}</div>}

        <div className="row">
          <button type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create event'}
          </button>
          <button type="button" className="ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** "45.5416, 10.2118" and "45.5416 10.2118" both mean the same thing to a person. */
function parseCentre(raw: string): [number, number] | null {
  const parts = raw.split(/[,\s]+/).filter(Boolean).map(Number)
  if (parts.length !== 2 || parts.some(Number.isNaN)) return null
  const [lat, lon] = parts
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null
  return [lat, lon]
}

function parseFactions(raw: string) {
  const names = raw.split(',').map((s) => s.trim()).filter(Boolean)
  if (names.length === 0) return undefined
  return names.map((name, index) => ({
    // The key travels in realtime topic names, so it has to survive being put in a URL.
    key: name.toLowerCase().replace(/[^a-z0-9]+/g, '') || `side${index + 1}`,
    name,
    displayOrder: index + 1,
  }))
}
